import React from 'react';
import {
  requireDecisionReceiptsForCandidateKeys,
  RECEIPT_ACTIONS,
} from '../reviewableItems/reviewableDecisionReceipts';
import {
  buildReviewableCandidatesFromCareSource,
  getStructuredCandidateDestination,
} from './careSourceReviewableCandidates';
import {
  buildManualReviewQueueSummary,
  buildManualReviewSourceSummary,
} from './profileReviewQueueModel';
import { getCareSourceContext } from './careSourceStatusModel';

const HANDLED_RECEIPT_ACTIONS = new Set([
  RECEIPT_ACTIONS.RESOLVED,
  RECEIPT_ACTIONS.IGNORED,
  RECEIPT_ACTIONS.AUTO_APPLIED,
]);

export const buildCandidatesBySource = (sources = [], handledKeys = new Set()) => (
  Object.fromEntries(sources.map((source) => [
    source.id,
    buildReviewableCandidatesFromCareSource(source, {
      includeMedication: getCareSourceContext(source) !== 'medication',
    })
      .filter((candidate) => !handledKeys.has(candidate.key)),
  ]))
);

export const buildStructuredDestinationsBySource = (sources = [], candidatesBySource = {}) => (
  Object.fromEntries(sources.map((source) => {
    const destinations = (candidatesBySource[source.id] || []).reduce((counts, candidate) => {
      const destination = getStructuredCandidateDestination(candidate.domain);
      counts[destination] = (counts[destination] || 0) + 1;
      return counts;
    }, {});
    return [source.id, destinations];
  }))
);

export const buildManualReviewViewModel = (sources = [], handledKeys = new Set()) => {
  const candidatesBySource = buildCandidatesBySource(sources, handledKeys);
  const structuredDestinationsBySource = buildStructuredDestinationsBySource(
    sources,
    candidatesBySource
  );
  return {
    candidatesBySource,
    structuredDestinationsBySource,
    summary: buildManualReviewQueueSummary(sources, structuredDestinationsBySource),
  };
};

export const useManualReviewQueueModel = (sources = []) => {
  const [handledKeys, setHandledKeys] = React.useState(new Set());
  const [receiptsReady, setReceiptsReady] = React.useState(false);
  const [receiptError, setReceiptError] = React.useState(null);
  const [loadedSignature, setLoadedSignature] = React.useState(null);
  const requestVersion = React.useRef(0);
  const [visibleOverrides, setVisibleOverrides] = React.useState({});
  const candidateKeys = React.useMemo(() => (
    [...new Set(sources.flatMap(
      (source) => buildReviewableCandidatesFromCareSource(source, { includeMedication: true })
        .filter((candidate) => getCareSourceContext(source) !== 'medication' || candidate.domain !== 'medication')
        .map((candidate) => candidate.key)
        .filter(Boolean)
    ))].sort()
  ), [sources]);
  const candidateKeySignature = candidateKeys.join('|');

  React.useEffect(() => {
    setVisibleOverrides({});
  }, [candidateKeySignature]);

  const refreshReceipts = React.useCallback(() => {
    const version = ++requestVersion.current;
    let active = true;
    setReceiptError(null);
    setReceiptsReady(false);
    Promise.resolve().then(() => requireDecisionReceiptsForCandidateKeys(candidateKeys))
      .then((receipts) => {
        if (!active || requestVersion.current !== version) return;
        setHandledKeys(new Set((receipts || [])
          .filter((receipt) => HANDLED_RECEIPT_ACTIONS.has(receipt.action))
          .map((receipt) => receipt.candidateKey || receipt.id)
          .filter(Boolean)));
        setLoadedSignature(candidateKeySignature);
        setReceiptsReady(true);
      })
      .catch((error) => {
        if (!active || requestVersion.current !== version) return;
        setReceiptError(error);
        setReceiptsReady(false);
      });
    return () => {
      active = false;
    };
  }, [candidateKeySignature]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => refreshReceipts(), [refreshReceipts]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleReviewChange = () => refreshReceipts();
    window.addEventListener('captureez:care-knowledge-refresh', handleReviewChange);
    return () => window.removeEventListener('captureez:care-knowledge-refresh', handleReviewChange);
  }, [refreshReceipts]);

  const ready = receiptsReady && loadedSignature === candidateKeySignature;
  const baseViewModel = React.useMemo(
    () => buildManualReviewViewModel(sources, handledKeys),
    [sources, handledKeys]
  );
  const candidatesBySource = baseViewModel.candidatesBySource;
  const structuredDestinationsBySource = React.useMemo(() => {
    const derived = baseViewModel.structuredDestinationsBySource;
    return Object.fromEntries(sources.map((source) => [
      source.id,
      ready
        ? (visibleOverrides[source.id]?.destinations || derived[source.id] || {})
        : {},
    ]));
  }, [sources, baseViewModel.structuredDestinationsBySource, ready, visibleOverrides]);
  const summary = React.useMemo(
    () => buildManualReviewQueueSummary(sources, structuredDestinationsBySource),
    [sources, structuredDestinationsBySource]
  );
  const pendingSourceIds = React.useMemo(
    () => new Set(sources
      .filter((source) => buildManualReviewSourceSummary(source, {
        structuredDestinations: structuredDestinationsBySource[source.id],
      }).pendingTotal > 0)
      .map((source) => source.id)),
    [sources, structuredDestinationsBySource]
  );
  const updateVisibleCandidates = React.useCallback((sourceId, result = {}) => {
    if (!sourceId) return;
    setVisibleOverrides((current) => {
      const next = {
        count: Number(result.count || 0),
        destinations: result.destinations || {},
      };
      if (JSON.stringify(current[sourceId] || {}) === JSON.stringify(next)) return current;
      return { ...current, [sourceId]: next };
    });
  }, []);

  return {
    candidatesBySource: ready
      ? candidatesBySource
      : Object.fromEntries(sources.map((source) => [source.id, []])),
    structuredDestinationsBySource,
    pendingSourceIds,
    receiptsReady: ready,
    receiptError,
    retryReceipts: refreshReceipts,
    summary,
    updateVisibleCandidates,
  };
};

export default useManualReviewQueueModel;
